/**
 * Stale-deploy recovery for Next.js code-split chunks.
 *
 * When a new deployment ships, content-hashed chunk filenames change. A client
 * that loaded the page before the deploy then lazy-loads a route chunk whose
 * filename no longer exists on the CDN → `ChunkLoadError`. The fix is a one-time
 * hard reload to pull the current deployment's HTML + chunk manifest.
 *
 * A cooldown guards against a reload loop (e.g. a chunk that 404s on the new
 * deploy too): we only auto-reload if we have not already done so very recently.
 */

/** Session-storage key holding the epoch-ms of the last auto-reload. */
export const CHUNK_RELOAD_KEY = 'pp:chunk-reload-at';

/** Minimum gap between auto-reloads. Below this we stop, to avoid a reload loop. */
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

type ErrorLike = { name?: string; message?: string } | null | undefined;

export function isChunkLoadError(error: ErrorLike): boolean {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = error.message ?? '';
  return /ChunkLoadError|Failed to load chunk|Loading chunk \d+ failed|error loading dynamically imported module/i.test(
    message,
  );
}

export function shouldReloadOnChunkError(
  error: ErrorLike,
  nowMs: number,
  lastReloadAtMs: number | null,
): boolean {
  if (!isChunkLoadError(error)) return false;
  if (lastReloadAtMs !== null && nowMs - lastReloadAtMs < CHUNK_RELOAD_COOLDOWN_MS) return false;
  return true;
}
